import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, Tag, ArrowRight, ShoppingCart } from 'lucide-react';
import Header from './components/Header1';
import Footer from './components/Footer1';
import Button from './components/Button';

const Cart = () => {
    const navigate = useNavigate();
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);

    const [cartItems, setCartItems] = useState([
        {
            id: 1, name: 'Chanderi Silk Saree', price: 8999, quantity: 1, size: 'Free Size',
            image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400',
        },
        {
            id: 2, name: 'Banarasi Brocade Lehenga', price: 24999, quantity: 1, size: 'M',
            image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400',
        },
        {
            id: 7, name: 'Cotton Chikankari Kurta', price: 2999, quantity: 2, size: 'L',
            image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400',
        },
    ]);

    const updateQuantity = (id, delta) => {
        setCartItems(items =>
            items.map(item =>
                item.id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item
            )
        );
    };

    const removeItem = (id) => {
        setCartItems(items => items.filter(item => item.id !== id));
    };

    const applyCoupon = () => {
        if (couponCode.toUpperCase() === 'PRIYA10') {
            setAppliedCoupon({ code: 'PRIYA10', discount: 10 });
        } else {
            alert('Invalid coupon code');
        }
    };

    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const discount = appliedCoupon ? (subtotal * appliedCoupon.discount) / 100 : 0;
    const shipping = subtotal > 5000 ? 0 : 199;
    const total = subtotal - discount + shipping;

    return (
        <div className="min-h-screen bg-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
            <Header cartCount={cartItems.length} />

            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-10 lg:py-12">
                <h1 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-6 md:mb-8">Shopping Cart</h1>

                {cartItems.length === 0 ? (
                    <div className="text-center py-16">
                        <ShoppingCart size={56} className="text-[#D1D5DB] mx-auto mb-4" />
                        <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-2">Your cart is empty</h2>
                        <p className="text-sm text-[#6B7280] mb-6">Discover our beautiful collection of ethnic wear</p>
                        <Button variant="primary" onClick={() => navigate('/template1/shop')}>
                            Continue Shopping
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                        {/* Cart Items */}
                        <div className="lg:col-span-2 space-y-3 md:space-y-4">
                            {cartItems.map(item => (
                                <div key={item.id} className="flex gap-3 md:gap-4 p-3 md:p-4 border border-[#E5E7EB]">
                                    <div className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0 bg-[#F8FAF8] overflow-hidden">
                                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm md:text-base font-medium text-[#1A1A1A] mb-0.5 truncate">{item.name}</h3>
                                        <p className="text-xs text-[#6B7280] mb-1">Size: {item.size}</p>
                                        <p className="text-sm md:text-base font-semibold text-[#2D6A4F]">{'\u20B9'}{item.price.toLocaleString('en-IN')}</p>
                                    </div>
                                    <div className="flex flex-col justify-between items-end">
                                        <button onClick={() => removeItem(item.id)} className="text-[#9CA3AF] hover:text-red-500 transition-colors">
                                            <Trash2 size={16} />
                                        </button>
                                        <div className="flex items-center border border-[#E5E7EB]">
                                            <button onClick={() => updateQuantity(item.id, -1)} className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center hover:bg-[#F8FAF8] text-[#1A1A1A]">
                                                <Minus size={12} />
                                            </button>
                                            <span className="w-7 md:w-8 text-center text-xs md:text-sm font-medium text-[#1A1A1A]">{item.quantity}</span>
                                            <button onClick={() => updateQuantity(item.id, 1)} className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center hover:bg-[#F8FAF8] text-[#1A1A1A]">
                                                <Plus size={12} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Order Summary */}
                        <div className="lg:col-span-1">
                            <div className="border border-[#E5E7EB] p-5 md:p-6 sticky top-24">
                                <h2 className="text-lg font-semibold text-[#1A1A1A] mb-5">Order Summary</h2>

                                {/* Coupon */}
                                <div className="mb-5">
                                    <label className="block text-xs font-medium text-[#1A1A1A] mb-2">Coupon Code</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text" value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value)}
                                            placeholder="Enter code"
                                            className="flex-1 px-3 py-2 border border-[#E5E7EB] text-sm focus:outline-none focus:border-[#2D6A4F]"
                                        />
                                        <button onClick={applyCoupon} className="px-3 py-2 border border-[#E5E7EB] hover:border-[#2D6A4F] transition-colors">
                                            <Tag size={16} className="text-[#2D6A4F]" />
                                        </button>
                                    </div>
                                    {appliedCoupon && (
                                        <p className="text-xs text-green-600 mt-1.5">"{appliedCoupon.code}" applied! {appliedCoupon.discount}% off</p>
                                    )}
                                    <p className="text-[10px] text-[#9CA3AF] mt-1">Try: PRIYA10 for 10% off</p>
                                </div>

                                {/* Totals */}
                                <div className="space-y-2.5 mb-5 pb-5 border-b border-[#E5E7EB]">
                                    <div className="flex justify-between text-sm text-[#6B7280]">
                                        <span>Subtotal</span>
                                        <span>{'\u20B9'}{subtotal.toLocaleString('en-IN')}</span>
                                    </div>
                                    {appliedCoupon && (
                                        <div className="flex justify-between text-sm text-green-600">
                                            <span>Discount ({appliedCoupon.discount}%)</span>
                                            <span>-{'\u20B9'}{discount.toLocaleString('en-IN')}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-sm text-[#6B7280]">
                                        <span>Shipping</span>
                                        <span>{shipping === 0 ? 'Free' : `\u20B9${shipping}`}</span>
                                    </div>
                                    {shipping === 0 && <p className="text-[10px] text-[#9CA3AF]">Free shipping on orders over {'\u20B9'}5,000</p>}
                                </div>

                                <div className="flex justify-between text-lg font-semibold text-[#1A1A1A] mb-5">
                                    <span>Total</span>
                                    <span>{'\u20B9'}{total.toLocaleString('en-IN')}</span>
                                </div>

                                <Button variant="primary" size="lg" fullWidth onClick={() => navigate('/template1/checkout')}>
                                    Proceed to Checkout <ArrowRight size={16} className="ml-1" />
                                </Button>

                                <button
                                    onClick={() => navigate('/template1/shop')}
                                    className="w-full text-center text-sm text-[#6B7280] hover:text-[#2D6A4F] mt-3 transition-colors"
                                >
                                    Continue Shopping
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
};

export default Cart;
